import { useState, useCallback, useMemo, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { Receipt, SortConfig, SortKey, SettingItem, BulkUpdatePayload, FilterGroup } from '../types';
import { generateReceiptFileName } from '../services/utils';
import { useReceiptsData } from './useReceiptsData';
import { useFileUpload } from './useFileUpload';
import { useImportExport } from './useImportExport';
import { ExportableColumn } from '../components/ExportModal';
import { useSettings } from './useSettings';
import { DATE_RANGE_FILTER_PREF_KEY } from '../types';
import { useAuth } from './useAuth';
import { logger } from '../services/logger';


type View = 'receipts' | 'dashboard' | 'pricetracker' | 'processing' | 'dataManagement' | 'appSettings';

export interface DuplicateInfo {
    existingReceipt: Receipt;
    newReceipt: Receipt;
    resolve: (overwrite: boolean) => void;
}

export const useReceiptsManager = () => {
  const { user } = useAuth();
  // --- UI State ---
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState<View>('receipts');
  const [currentPage, setCurrentPage] = useState(1);
  const [receiptToDelete, setReceiptToDelete] = useState<Receipt | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null);
  const [selectedUploadTags, setSelectedUploadTags] = useState<SettingItem[]>([]);
  const [filters, setFilters] = useState({
      tags: { include: [], exclude: [] } as FilterGroup,
      businessNames: { include: [], exclude: [] } as FilterGroup,
      categories: { include: [], exclude: [] } as FilterGroup,
      paymentMethods: { include: [], exclude: [] } as FilterGroup,
      currencies: { include: [], exclude: [] } as FilterGroup,
      dateFrom: null as string | null,
      dateTo: null as string | null,
  });
  const [selectedReceiptIds, setSelectedReceiptIds] = useState<Set<string>>(new Set());
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);
  const [isFilterInitialized, setIsFilterInitialized] = useState(false);
  const [receiptsSummary, setReceiptsSummary] = useState<{ currency: string, total: number }[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  
  const settings = useSettings();
  
  useEffect(() => {
    const loadDateFilter = async () => {
        const savedRange = await apiService.getPreference<{ from: string | null; to: string | null }>(DATE_RANGE_FILTER_PREF_KEY, null);
        if (savedRange) {
            setFilters(prev => ({
                ...prev,
                dateFrom: savedRange.from,
                dateTo: savedRange.to,
            }));
        } else {
            const now = new Date();
            const from = new Date(now.getFullYear(), now.getMonth(), 1);
            const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            const formatDateISO = (date: Date): string => date.toISOString().split('T')[0];
            setFilters(prev => ({
                ...prev,
                dateFrom: formatDateISO(from),
                dateTo: formatDateISO(to),
            }));
        }
        setIsFilterInitialized(true);
    };
    loadDateFilter();
  }, []);

  useEffect(() => {
    if (isFilterInitialized) {
        apiService.setPreference(DATE_RANGE_FILTER_PREF_KEY, { from: filters.dateFrom, to: filters.dateTo });
    }
  }, [filters.dateFrom, filters.dateTo, isFilterInitialized]);

  useEffect(() => {
    const handler = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
        clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const fetchSummary = async () => {
        if (!isFilterInitialized || currentView !== 'receipts') {
            setReceiptsSummary([]);
            return;
        };

        try {
            const summary = await apiService.getReceiptsSummary({
                searchTerm: debouncedSearchTerm,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                tags_include: filters.tags.include,
                tags_exclude: filters.tags.exclude,
                businessNames_include: filters.businessNames.include,
                businessNames_exclude: filters.businessNames.exclude,
                categories_include: filters.categories.include,
                categories_exclude: filters.categories.exclude,
                paymentMethods_include: filters.paymentMethods.include,
                paymentMethods_exclude: filters.paymentMethods.exclude,
                currencies_include: filters.currencies.include,
                currencies_exclude: filters.currencies.exclude,
            });
            setReceiptsSummary(summary);
        } catch (error) {
            console.error("Nepodařilo se načíst souhrn účtenek:", error);
            setReceiptsSummary([]);
        }
    };

    fetchSummary();

  }, [debouncedSearchTerm, filters, isFilterInitialized, currentView]);


  useEffect(() => {
    setCurrentPage(1);
    setSelectedReceiptIds(new Set());
    setIsSelectAllActive(false);
  }, [searchTerm, currentView, sortConfig, filters]);
  
  useEffect(() => {
    if (!isSelectAllActive) {
      setSelectedReceiptIds(new Set());
    }
  }, [currentPage, isSelectAllActive]);

  // --- Data a operace ---
  const { receipts, setReceipts, totalReceipts, totalPages, isLoading, error: dataError, fetchReceipts } = useReceiptsData({
    currentView, currentPage, searchTerm, sortConfig, filters, isReady: isFilterInitialized,
  });
  
  const filterOptions = useMemo(() => ({
      businessNames: settings.businessNames,
      categories: settings.categories,
      paymentMethods: settings.paymentMethods,
      tags: settings.tags,
      currencies: settings.currencies,
  }), [settings.businessNames, settings.categories, settings.paymentMethods, settings.tags, settings.currencies]);

  
  const onActionComplete = useCallback(() => {
    fetchReceipts();
    settings.refresh();
    setSelectedReceiptIds(new Set());
  }, [fetchReceipts, settings]);

  const onUploadComplete = useCallback(() => {
    // Zůstaneme na aktuálním pohledu dle požadavku uživatele.
    // Uživatel může přejít do sekce "Zpracování" manuálně pro sledování stavu.
  }, []);
  
  const promptForDuplicate = useCallback((existingReceipt: Receipt, newReceipt: Receipt) => {
    return new Promise<boolean>((resolve) => {
        setDuplicateInfo({ existingReceipt, newReceipt, resolve });
    });
  }, []);

  const { isProcessing: isUploading, uploadProgress, failedUploads, setFailedUploads, error: uploadError, handleFilesSelected, retryFailedUploads } = useFileUpload(
      onUploadComplete
  );
  
  const { isProcessing: isImportExporting, error: importExportError, handleFileImport, handleExport } = useImportExport({
    filters: { 
        dateFrom: filters.dateFrom, 
        dateTo: filters.dateTo, 
        searchTerm, 
        tags_include: filters.tags.include,
        tags_exclude: filters.tags.exclude,
        businessNames_include: filters.businessNames.include,
        businessNames_exclude: filters.businessNames.exclude,
        categories_include: filters.categories.include,
        categories_exclude: filters.categories.exclude,
        paymentMethods_include: filters.paymentMethods.include,
        paymentMethods_exclude: filters.paymentMethods.exclude,
        currencies_include: filters.currencies.include,
        currencies_exclude: filters.currencies.exclude,
    },
    onActionComplete: onActionComplete,
  });

  const isProcessing = isUploading || isImportExporting;
  const error = dataError || uploadError || importExportError;

  const handleClearSelection = useCallback(() => {
    setSelectedReceiptIds(new Set());
    setIsSelectAllActive(false);
  }, []);
  
  const handleClearAllFilters = useCallback(() => {
    setFilters(prev => ({
        ...prev, // preserve dateFrom, dateTo
        tags: { include: [], exclude: [] },
        businessNames: { include: [], exclude: [] },
        categories: { include: [], exclude: [] },
        paymentMethods: { include: [], exclude: [] },
        currencies: { include: [], exclude: [] },
    }));
  }, []);

  const handleSort = useCallback((key: SortKey) => {
    setSortConfig(prev => (prev?.key === key && prev.direction === 'asc') ? { key, direction: 'desc' } : (prev?.key === key) ? null : { key, direction: 'asc' });
  }, []);

  const handleUpdateReceipt = useCallback(async (updatedReceipt: Receipt) => {
    logger.debug('[handleUpdateReceipt] Obdržena účtenka k aktualizaci:', updatedReceipt);

    let parsedJsonData: any = {};
    try {
        parsedJsonData = JSON.parse(updatedReceipt.jsonData || '{}');
    } catch (e) {
        logger.error("Chyba při parsování jsonData při aktualizaci", e);
    }
    
    // Pro generování jména použijeme nová data, ale pokud chybí částka (je null/undefined),
    // zkusíme použít jako zálohu starou hodnotu z jsonData.
    const dataForFileName = {
        ...updatedReceipt,
        amount: updatedReceipt.amount ?? parsedJsonData.totalAmount,
    };
    logger.debug('[handleUpdateReceipt] Data použitá pro generování názvu souboru:', dataForFileName);

    const newFileName = generateReceiptFileName(dataForFileName);
    
    const finalReceipt = {
        ...updatedReceipt,
        jsonData: JSON.stringify({
            ...parsedJsonData,
            suggestedFileName: newFileName
        }, null, 2),
    };
    logger.debug('[handleUpdateReceipt] Finální objekt účtenky odesílaný na API:', finalReceipt);
    
    setReceipts(current => current.map(r => (r.id === finalReceipt.id ? finalReceipt : r)));
    await apiService.updateReceipt(finalReceipt);
    
    await settings.refresh();
  }, [setReceipts, settings]);
  
  const handleDeleteReceipt = useCallback((receipt: Receipt) => setReceiptToDelete(receipt), []);

  const handleConfirmDelete = useCallback(async () => {
    if (!receiptToDelete) return;
    await apiService.deleteReceipt(receiptToDelete.id);
    setReceiptToDelete(null); 
    if (receipts.length === 1 && currentPage > 1) {
        setCurrentPage(p => p - 1);
    } else {
        await fetchReceipts();
    }
  }, [receiptToDelete, receipts.length, currentPage, fetchReceipts]);

  const handleCancelDelete = useCallback(() => setReceiptToDelete(null), []);
  
  const handleExportWrapper = useCallback(async (columns: ExportableColumn[]) => {
      await handleExport(columns);
      setIsExportModalOpen(false);
  }, [handleExport]);
  
  const handleFilesSelectedWrapper = useCallback((files: File[]) => handleFilesSelected(files, selectedUploadTags), [handleFilesSelected, selectedUploadTags]);
  const handleFileImportWrapper = useCallback((file: File) => handleFileImport(file, selectedUploadTags), [handleFileImport, selectedUploadTags]);
  const retryFailedUploadsWrapper = useCallback(() => retryFailedUploads(selectedUploadTags), [retryFailedUploads, selectedUploadTags]);

  const handleDuplicateConfirm = () => { if (duplicateInfo) { duplicateInfo.resolve(true); setDuplicateInfo(null); } };
  const handleDuplicateSkip = () => { if (duplicateInfo) { duplicateInfo.resolve(false); setDuplicateInfo(null); } };

  const handleToggleSelect = useCallback((receiptId: string) => {
    setSelectedReceiptIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(receiptId)) {
            newSet.delete(receiptId);
        } else {
            newSet.add(receiptId);
        }
        
        if (newSet.size < totalReceipts) {
            setIsSelectAllActive(false);
        } else if (newSet.size === totalReceipts && totalReceipts > 0) {
            setIsSelectAllActive(true);
        }
        return newSet;
    });
  }, [totalReceipts]);

  const handleToggleSelectAll = useCallback(() => {
    const currentIdsOnPage = new Set(receipts.map(r => r.id));
    const areAllSelectedOnPage = receipts.length > 0 && receipts.every(r => selectedReceiptIds.has(r.id));

    setSelectedReceiptIds(prev => {
        const newSet = new Set(prev);
        if (areAllSelectedOnPage) {
            currentIdsOnPage.forEach(id => newSet.delete(id));
        } else {
            currentIdsOnPage.forEach(id => newSet.add(id));
        }

        if (newSet.size < totalReceipts) {
            setIsSelectAllActive(false);
        } else if (newSet.size === totalReceipts && totalReceipts > 0) {
            setIsSelectAllActive(true);
        }
        return newSet;
    });
  }, [receipts, selectedReceiptIds, totalReceipts]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedReceiptIds.size === 0) return;
    await apiService.bulkDeleteReceipts(Array.from(selectedReceiptIds));
    
    handleClearSelection();

    const selectedOnPageCount = receipts.filter(r => selectedReceiptIds.has(r.id)).length;
    const remainingOnPage = receipts.length - selectedOnPageCount;
    if (remainingOnPage === 0 && currentPage > 1) {
        setCurrentPage(p => p - 1);
    } else {
        await fetchReceipts();
    }
  }, [selectedReceiptIds, receipts, currentPage, fetchReceipts, handleClearSelection]);

  const handleBulkUpdate = useCallback(async (updates: BulkUpdatePayload) => {
      if (selectedReceiptIds.size === 0) return;
      await apiService.bulkUpdateReceipts(Array.from(selectedReceiptIds), updates);
      handleClearSelection();
      await fetchReceipts();
      await settings.refresh(); 
  }, [selectedReceiptIds, fetchReceipts, settings, handleClearSelection]);
  
  const handleSelectAllFiltered = useCallback(async () => {
    try {
        const { ids } = await apiService.getFilteredReceiptIds({
            searchTerm,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            tags_include: filters.tags.include,
            tags_exclude: filters.tags.exclude,
            businessNames_include: filters.businessNames.include,
            businessNames_exclude: filters.businessNames.exclude,
            categories_include: filters.categories.include,
            categories_exclude: filters.categories.exclude,
            paymentMethods_include: filters.paymentMethods.include,
            paymentMethods_exclude: filters.paymentMethods.exclude,
            currencies_include: filters.currencies.include,
            currencies_exclude: filters.currencies.exclude,
        });
        setSelectedReceiptIds(new Set(ids));
        setIsSelectAllActive(true);
    } catch (error) {
        console.error("Nepodařilo se vybrat všechny filtrované účtenky:", error);
    }
  }, [searchTerm, filters]);


  return {
    receipts, isLoading, isProcessing, error, isEditMode, setIsEditMode, isExportModalOpen, setIsExportModalOpen,
    searchTerm, setSearchTerm, currentView, setCurrentView,
    currentPage, setCurrentPage, totalPages, uploadProgress, failedUploads, setFailedUploads,
    receiptToDelete, duplicateInfo, sortConfig, selectedUploadTags, setSelectedUploadTags,
    filters, setFilters, filterOptions,
    selectedReceiptIds, setSelectedReceiptIds, handleToggleSelect, handleToggleSelectAll, handleBulkDelete, handleBulkUpdate,
    handleFilesSelected: handleFilesSelectedWrapper, handleUpdateReceipt, handleDeleteReceipt, handleConfirmDelete,
    handleCancelDelete, handleFileImport: handleFileImportWrapper, a: handleExportWrapper,
    retryFailedUploads: retryFailedUploadsWrapper, handleSort, handleDuplicateConfirm, handleDuplicateSkip,
    totalReceipts,
    isSelectAllActive,
    handleSelectAllFiltered,
    handleClearSelection,
    handleClearAllFilters,
    handleExport: handleExportWrapper,
    receiptsSummary,
  };
};